import React, { useState, useEffect } from "react";
import { Building2, CheckCircle2, Phone, MessageCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import PublicLayout from "@/components/layout/PublicLayout";
import PageHeader from "@/components/PageHeader";
import api, { trackEvent, formatApiErrorDetail } from "@/lib/api";
import { useSite, waLink } from "@/context/SiteContext";

const STEPS = ["Employer", "Manpower Requirement", "Dreamzz HR", "Candidate Database", "Shortlisting", "Interview", "Placement"];

export default function ForEmployers() {
  const { settings } = useSite();
  const [industries, setIndustries] = useState([]);
  const [form, setForm] = useState({ company: "", contact_person: "", phone: "", email: "", industry: "", location: "", job_role: "", vacancies: "", experience: "", salary_range: "", joining_date: "", additional: "" });
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => { api.get("/industries").then((r) => setIndustries(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", form);
      trackEvent("generate_lead", { company: form.company });
      toast.success("Requirement submitted! Our recruitment team will contact you shortly.");
      setForm({ company: "", contact_person: "", phone: "", email: "", industry: "", location: "", job_role: "", vacancies: "", experience: "", salary_range: "", joining_date: "", additional: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit"); }
    finally { setLoading(false); }
  };

  return (
    <PublicLayout waMessage="Hi Dreamzz HR & Consultancy, I would like to discuss my manpower requirement.">
      <Seo title="For Employers — Hire Talent" description="Submit your manpower requirement and hire skilled, semi-skilled and professional staff across Karnataka with Dreamzz HR & Consultancy." />
      <PageHeader eyebrow="For Employers" title="Need Staff? Let's Build Your Team." subtitle="Share your manpower requirement and our recruitment team will deliver the right candidates — fast." />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-[1fr_1.1fr] gap-10">
        <div>
          <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Building2 className="text-brand-blue" />How Hiring Works</h2>
          <div className="mt-6 space-y-3">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-4 glass rounded-xl p-4">
                <span className="grid place-items-center h-9 w-9 rounded-lg bg-brand-blue/20 text-brand-blue font-mono font-bold shrink-0">{i + 1}</span>
                <span className="font-medium">{s}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 glass rounded-xl p-5">
            <p className="text-sm text-slate-400">Prefer to talk directly?</p>
            <div className="mt-3 flex flex-wrap gap-3">
              <a href={`tel:${settings.phone}`} onClick={() => trackEvent("phone_click")}><Button variant="outline" className="border-white/15"><Phone size={16} className="mr-2" />Call Team</Button></a>
              <a href={waLink(settings.whatsapp, "Hi Dreamzz HR & Consultancy, I would like to discuss my manpower requirement.")} target="_blank" rel="noreferrer" onClick={() => trackEvent("whatsapp_click")}>
                <Button variant="outline" className="border-[#25D366]/40 text-[#25D366]"><MessageCircle size={16} className="mr-2" />WhatsApp</Button></a>
            </div>
          </div>
        </div>

        <div className="glass-strong rounded-2xl p-6 sm:p-8">
          <h2 className="font-display text-xl font-bold mb-5">Submit Hiring Requirement</h2>
          <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
            <div><Label>Company *</Label><Input data-testid="lead-company" required value={form.company} onChange={upd("company")} className="bg-white/5 border-white/10 mt-1" /></div>
            <div><Label>Contact Person *</Label><Input data-testid="lead-contact" required value={form.contact_person} onChange={upd("contact_person")} className="bg-white/5 border-white/10 mt-1" /></div>
            <div><Label>Phone *</Label><Input data-testid="lead-phone" required value={form.phone} onChange={upd("phone")} className="bg-white/5 border-white/10 mt-1" /></div>
            <div><Label>Email</Label><Input data-testid="lead-email" type="email" value={form.email} onChange={upd("email")} className="bg-white/5 border-white/10 mt-1" /></div>
            <div>
              <Label>Industry</Label>
              <Select value={form.industry} onValueChange={(v) => setForm((f) => ({ ...f, industry: v }))}>
                <SelectTrigger data-testid="lead-industry" className="bg-white/5 border-white/10 mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="glass-strong border-white/10 max-h-60">{industries.map((i) => <SelectItem key={i.slug} value={i.name}>{i.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Location</Label><Input data-testid="lead-location" value={form.location} onChange={upd("location")} className="bg-white/5 border-white/10 mt-1" /></div>
            <div><Label>Job Role</Label><Input data-testid="lead-role" value={form.job_role} onChange={upd("job_role")} className="bg-white/5 border-white/10 mt-1" /></div>
            <div><Label>No. of Vacancies</Label><Input data-testid="lead-vacancies" value={form.vacancies} onChange={upd("vacancies")} className="bg-white/5 border-white/10 mt-1" /></div>
            <div><Label>Experience</Label><Input data-testid="lead-experience" value={form.experience} onChange={upd("experience")} className="bg-white/5 border-white/10 mt-1" /></div>
            <div><Label>Salary Range</Label><Input data-testid="lead-salary" value={form.salary_range} onChange={upd("salary_range")} className="bg-white/5 border-white/10 mt-1" /></div>
            <div className="sm:col-span-2"><Label>Required Joining Date</Label><Input data-testid="lead-joining" type="date" value={form.joining_date} onChange={upd("joining_date")} className="bg-white/5 border-white/10 mt-1" /></div>
            <div className="sm:col-span-2"><Label>Additional Requirements</Label><Textarea data-testid="lead-additional" value={form.additional} onChange={upd("additional")} rows={3} className="bg-white/5 border-white/10 mt-1" /></div>
            <Button data-testid="lead-submit" type="submit" disabled={loading} className="sm:col-span-2 btn-press bg-brand-emerald hover:bg-[#C62828] text-white font-semibold h-12">
              {loading ? "Submitting..." : "Submit Requirement"}
            </Button>
          </form>
        </div>
      </section>
    </PublicLayout>
  );
}
